import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { statsApi, indicatorsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const fmt = (v) => v === null || v === undefined ? '—' : Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 });

export default function Benchmark() {
  const { user } = useAuth();
  const [bench, setBench] = useState(null);
  const [inds, setInds] = useState({});
  const [evolutions, setEvolutions] = useState({});

  useEffect(() => {
    Promise.all([
      statsApi.benchmark(user.id),
      indicatorsApi.list(),
    ]).then(([b, i]) => {
      setBench(b.data);
      const map = {}; i.data.forEach(x => { map[x.code] = x; });
      setInds(map);
      // Cargar evoluciones para top 4
      const codes = b.data.benchmark?.slice(0, 4).map(x => x.indicator_code) || [];
      Promise.all(codes.map(c => statsApi.evolution(c, user.id))).then(results => {
        const evs = {}; results.forEach((r, i) => { evs[codes[i]] = r.data; });
        setEvolutions(evs);
      });
    }).catch(() => {});
  }, [user.id]);

  if (!bench) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-aea-blue" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Card className="aea-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><TrendingUp className="w-5 h-5 text-aea-blue" /> Comparativa con el sector · {bench.period_label || 'último periodo'}</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Todas las comparativas se muestran de forma agregada y anonimizada. Nunca se revelan datos individuales de otras empresas.</p>
        </CardHeader>
        <CardContent>
          {bench.benchmark?.length === 0 ? (
            <div className="text-center py-10 text-slate-500">Sin datos suficientes. Suba sus datos para ver comparativa.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Indicador</th>
                    <th className="text-right p-3">Su valor</th>
                    <th className="text-right p-3">Media sector</th>
                    <th className="text-right p-3">Mediana</th>
                    <th className="text-right p-3">P25 / P75</th>
                    <th className="text-right p-3">Vd vs media</th>
                    <th className="text-center p-3">n</th>
                  </tr>
                </thead>
                <tbody>
                  {bench.benchmark.map((b) => {
                    const ind = inds[b.indicator_code];
                    const insuf = b.sector.insufficient_data;
                    return (
                      <tr key={b.indicator_code} className="border-t" data-testid={`bench-row-${b.indicator_code}`}>
                        <td className="p-3">
                          <div className="font-medium">{ind?.name || b.indicator_code}</div>
                          <div className="text-xs text-slate-400 font-mono">{b.indicator_code} · {b.unit}</div>
                        </td>
                        <td className="p-3 text-right font-mono font-semibold">{fmt(b.my_value)}</td>
                        <td className="p-3 text-right font-mono">{insuf ? '—' : fmt(b.sector.mean)}</td>
                        <td className="p-3 text-right font-mono">{insuf ? '—' : fmt(b.sector.median)}</td>
                        <td className="p-3 text-right font-mono text-xs text-slate-500">{insuf ? '—' : `${fmt(b.sector.p25)} / ${fmt(b.sector.p75)}`}</td>
                        <td className="p-3 text-right">
                          {b.vs_mean_pct === null ? <span className="text-slate-400">—</span> :
                            <span className={`inline-flex items-center gap-1 font-semibold ${b.vs_mean_pct > 0 ? 'text-emerald-600' : b.vs_mean_pct < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                              {b.vs_mean_pct > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : b.vs_mean_pct < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                              {b.vs_mean_pct > 0 ? '+' : ''}{b.vs_mean_pct.toFixed(1)}%
                            </span>}
                        </td>
                        <td className="p-3 text-center text-xs font-mono text-slate-500">{b.sector.n}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Evolución temporal (top 4 indicadores) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(evolutions).map(([code, ev]) => (
          <Card key={code} className="aea-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-outfit">{ev.indicator_name} <span className="text-xs text-slate-400 font-normal">({ev.unit})</span></CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: 200 }}>
                <ResponsiveContainer>
                  <LineChart data={ev.evolution.map(e => ({
                    label: `${e.period_label.slice(0,3)} ${e.year - 2000}`,
                    Vd: e.my_value, Sector: e.sector_mean,
                  }))} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Legend />
                    <Line type="monotone" dataKey="Vd" stroke="#0369A1" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Sector" stroke="#94A3B8" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
